import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { money, money2, pushToast, useAsync } from "../lib";
import { Empty, Skeleton, LoginGate } from "../ui";

// ใช้ร่วมกับรายการเดินบัญชีในหน้าบัญชี (Account.js)
export const WITHDRAW_STATUS = {
  CHECKING: { t: "รอตรวจสอบ", c: "pending" },
  PENDING: { t: "รอโอน", c: "pending" },
  APPROVED: { t: "โอนแล้ว", c: "win" },
  DISAPPROVED: { t: "ไม่อนุมัติ", c: "lose" },
};

// ถอนขั้นต่ำ (บาท) — เซิร์ฟเวอร์ตรวจซ้ำที่ requestwithdraw
export const MIN_WITHDRAW = 50;

export default function WithdrawPage({ balance, onDone }) {
  const { user, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const uid = user?.id;
  const [busy, setBusy] = useState(false);
  // ยอดที่กรอก: เก็บเป็นสตริงตัวเลขล้วน (ไม่มีทศนิยม) — ขั้นต่ำเท่ากับที่เซิร์ฟเวอร์บังคับ
  const [amount, setAmount] = useState("");
  const { data: history, loading, reload } = useAsync(
    () => (uid ? lottoApi.withdrawHistory(uid) : Promise.resolve([])),
    [uid],
  );

  if (!isAuthenticated)
    return (
      <LoginGate
        icon="fluent-emoji-flat:credit-card"
        title="เข้าสู่ระบบก่อนถอนเงิน"
        desc="แจ้งถอนยอดคงเหลือเข้าบัญชีของคุณได้ในไม่กี่คลิก"
        perks={["โอนเข้าบัญชีที่ผูกไว้","เห็นยอดที่ถอนได้ชัดเจน","ดูประวัติการถอนย้อนหลัง"]}
        onLogin={() => nav("/auth")}
      />
    );

  const submit = async () => {
    if (!canSubmit) return;
    if (!window.confirm(`แจ้งถอน ${money(amt)} บาท?`)) return;
    setBusy(true);
    try {
      const res = await lottoApi.withdraw(uid, amt);
      if (res?.success === true) {
        pushToast("แจ้งถอนสำเร็จ รอแอดมินอนุมัติ", "ok", 5000);
        setAmount("");
        onDone?.();
        reload();
      } else {
        pushToast(res?.message || "ถอนไม่สำเร็จ — อาจติดเงื่อนไขเทิร์น", "err", 6000);
      }
    } catch (e) {
      pushToast(e?.response?.data?.message || "เกิดข้อผิดพลาด", "err");
    } finally {
      setBusy(false);
    }
  };

  const maxAmount = Math.floor(Number(balance) || 0);
  const amt = amount === "" ? null : Number(amount);
  const amountError =
    amt === null ? "" : amt < MIN_WITHDRAW ? `ถอนขั้นต่ำ ${money(MIN_WITHDRAW)} บาท` : amt > maxAmount ? "เกินยอดที่ถอนได้" : "";
  const canSubmit = !busy && amt !== null && !amountError;
  // รับเฉพาะตัวเลข: ตัดทศนิยม (ตั้งแต่จุดเป็นต้นไป) และตัวอักษร/เครื่องหมายทิ้ง
  const onAmount = (e) => setAmount(e.target.value.split(".")[0].replace(/\D/g, "").replace(/^0+/, "").slice(0, 9));

  const rows = Array.isArray(history) ? history : history?.data || [];

  return (
    <div className="vx-money" style={{ marginTop: 18 }}>
      <header className="lx-card vx-pagehead vx-wd-head">
        <div className="vx-money-copy">
          <span className="vx-kicker">
            <Icon icon="fluent-emoji-flat:money-with-wings" width={16} height={16} />
            ถอนเงิน
          </span>
          <div className="vx-wd-label">ยอดถอนได้</div>
          <div className="vx-wd-bal">
            <span className="lx-mono">{money2(balance)}</span> <small>บาท</small>
          </div>
          <div className="vx-wd-to">
            <Icon icon="solar:card-bold-duotone" width={16} height={16} />
            เข้าบัญชี {user?.bank_name} · {user?.book_number} · {user?.book_name || user?.name}
          </div>
        </div>
        <div className="vx-wd-act">
          <label className="vx-wd-amt-label" htmlFor="wd-amount">
            ยอดที่ต้องการถอน (บาท)
          </label>
          <div className="vx-wd-amt">
            <input
              id="wd-amount"
              className="lx-mono"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder={`ขั้นต่ำ ${MIN_WITHDRAW}`}
              value={amount}
              onChange={onAmount}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              disabled={busy || maxAmount < MIN_WITHDRAW}
              aria-invalid={!!amountError}
            />
            <button
              type="button"
              className="vx-wd-all"
              disabled={busy || maxAmount < MIN_WITHDRAW}
              onClick={() => setAmount(String(maxAmount))}
            >
              ถอนทั้งหมด
            </button>
          </div>
          <div className={`vx-wd-hint${amountError ? " err" : ""}`}>
            {maxAmount < MIN_WITHDRAW
              ? `ยอดถอนได้ต่ำกว่าขั้นต่ำ ${MIN_WITHDRAW} บาท`
              : amountError || `ถอนได้เฉพาะจำนวนเต็ม (ไม่มีทศนิยม) ขั้นต่ำ ${MIN_WITHDRAW} บาท`}
          </div>
          <button className="lx-btn mint block" disabled={!canSubmit} onClick={submit}>
            <Icon icon="solar:upload-minimalistic-bold" width={18} />
            {busy ? "กำลังส่ง..." : amt && !amountError ? `แจ้งถอน ${money(amt)} บาท` : "แจ้งถอน"}
          </button>
        </div>
      </header>

      <section className="vx-block">
        <div className="vx-block-head">
          <div className="vx-block-title">
            <span className="vx-block-ic cat-b">
              <Icon icon="solar:history-bold-duotone" width={18} height={18} />
            </span>
            <h2 className="lx-h2">ประวัติถอน</h2>
          </div>
        </div>
        {loading && <Skeleton h={140} />}
        {!loading && !rows.length && (
          <div className="lx-card">
            <Empty icon="solar:document-text-bold-duotone">ยังไม่มีรายการ</Empty>
          </div>
        )}
        {!!rows.length && (
          <div className="vx-hist">
            {rows.slice(0, 20).map((h, i) => {
              const st = WITHDRAW_STATUS[h.status] || { t: h.status, c: "lose" };
              return (
                <div className="vx-hrow" key={h.withdrawRequestID || i}>
                  <span className="vx-hrow-ic out">
                    <Icon icon="solar:upload-minimalistic-bold" width={20} height={20} />
                  </span>
                  <div className="vx-hrow-tx">
                    <b>ถอนเงิน</b>
                    <small>{String(h.created_at || "").replace("T", " ").slice(0, 16)}</small>
                  </div>
                  <div className="vx-hrow-end">
                    <b className="lx-mono">{money(h.approveAmount || h.amount)}</b>
                    <span className={`lx-tag ${st.c}`}>{st.t}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
